import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router";
import { SUPPORTED_LANGUAGES } from "@/lib/languages";
import { LanguageContext } from "@/contexts/languageContextDef";

const STORAGE_KEY = "bloxida_selected_language";
const GOOGTRANS_COOKIE = "googtrans";

declare global {
  interface Window {
    google?: {
      translate?: {
        TranslateElement: new (
          options: {
            pageLanguage: string;
            includedLanguages?: string;
            autoDisplay?: boolean;
            layout?: unknown;
          },
          elementId: string
        ) => void;
      };
    };
    googleTranslateElementInit?: () => void;
  }
}

function setTranslateCookie(langCode: string) {
  const cookieValue = langCode === "en" ? "" : `/en/${langCode}`;
  const maxAge = langCode === "en" ? 0 : 31536000; // 1 year or delete
  const hostname = window.location.hostname;

  // Set for current host and parent domain
  document.cookie = `${GOOGTRANS_COOKIE}=${cookieValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
  if (hostname.includes(".")) {
    const domainParts = hostname.split(".");
    if (domainParts.length >= 2) {
      const rootDomain = domainParts.slice(-2).join(".");
      document.cookie = `${GOOGTRANS_COOKIE}=${cookieValue}; path=/; domain=.${rootDomain}; max-age=${maxAge}; SameSite=Lax`;
    }
  }
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguageState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) {
        return saved;
      }
    } catch {
      // Ignore localStorage read errors
    }
    return "en";
  });

  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  const currentLanguageObj = useMemo(() => {
    return SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) || SUPPORTED_LANGUAGES[0];
  }, [currentLanguage]);

  // Trigger Google Translate engine to apply translation
  const applyTranslation = useCallback((langCode: string) => {
    // If admin route, do not translate
    if (location.pathname.startsWith("/admin")) {
      return;
    }

    const select = document.querySelector<HTMLSelectElement>(".goog-te-combo");
    if (select) {
      const targetValue = langCode === "en" ? "" : langCode;
      if (select.value !== targetValue) {
        select.value = targetValue;
        select.dispatchEvent(new Event("change"));
      }
    }
  }, [location.pathname]);

  // Set Language Action
  const setLanguage = useCallback((code: string) => {
    const valid = SUPPORTED_LANGUAGES.some((l) => l.code === code);
    const targetCode = valid ? code : "en";

    setCurrentLanguageState(targetCode);
    try {
      localStorage.setItem(STORAGE_KEY, targetCode);
    } catch {
      // Ignore write errors
    }

    setTranslateCookie(targetCode);
    setIsLoading(true);

    // Apply translation to DOM
    applyTranslation(targetCode);

    // Update document dir for RTL languages
    const langObj = SUPPORTED_LANGUAGES.find((l) => l.code === targetCode);
    if (langObj?.dir) {
      document.documentElement.dir = langObj.dir;
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 400);
  }, [applyTranslation]);

  // Load Google Translate script once when needed
  useEffect(() => {
    if (typeof window === "undefined") return;

    window.googleTranslateElementInit = () => {
      if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: SUPPORTED_LANGUAGES.map((l) => l.code).join(","),
            autoDisplay: false,
          },
          "google_translate_element"
        );

        // Apply saved language after init
        if (currentLanguage !== "en") {
          setTimeout(() => {
            applyTranslation(currentLanguage);
          }, 300);
        }
      }
    };

    // Check if script is already present
    const existingScript = document.getElementById("google-translate-script");
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else if (window.google?.translate?.TranslateElement) {
      // Re-trigger if already loaded
      window.googleTranslateElementInit();
    }
  }, [applyTranslation, currentLanguage]);

  // Re-apply translation on SPA route change
  useEffect(() => {
    if (isAdminRoute) {
      // Ensure admin stays in raw English
      const select = document.querySelector<HTMLSelectElement>(".goog-te-combo");
      if (select && select.value !== "") {
        select.value = "";
        select.dispatchEvent(new Event("change"));
      }
      return;
    }

    if (currentLanguage !== "en") {
      const timer = setTimeout(() => {
        applyTranslation(currentLanguage);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, currentLanguage, isAdminRoute, applyTranslation]);

  const value = useMemo(
    () => ({
      currentLanguage,
      currentLanguageObj,
      setLanguage,
      isLoading,
      supportedLanguages: SUPPORTED_LANGUAGES,
    }),
    [currentLanguage, currentLanguageObj, setLanguage, isLoading]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
      {/* Hidden translation container required by Google Translate */}
      <div id="google_translate_element" className="hidden" aria-hidden="true" style={{ display: "none" }} />
    </LanguageContext.Provider>
  );
};
